import React, { useEffect, useState } from 'react';
import { prepAPI, applicantAPI } from '@/services/api';
import PrepChargeConfirmModal from '@/components/shared/PrepChargeConfirmModal';
import {
  Sparkles, ChevronRight, ChevronLeft, RotateCcw, Trash2, Mic, MicOff,
  CheckCircle2, AlertCircle, Loader2, Plus, Trophy, Brain, Target, MessageSquare,
} from 'lucide-react';

const TOUGHNESS_LABELS = ['Casual', 'Easy', 'Easy', 'Light', 'Moderate', 'Moderate', 'Challenging', 'Hard', 'Tough', 'Expert', 'Grandmaster'];

export default function ApplicantPrepSimulator() {
  const [view, setView] = useState('home'); // home | setup | session | result
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSessions = async () => {
    try {
      setLoading(true);
      const list = await prepAPI.list();
      setSessions(list || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load sessions');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadSessions(); }, []);

  const openSession = async (id) => {
    try {
      const s = await prepAPI.get(id);
      setActiveSession(s);
      setView(s.status === 'completed' ? 'result' : 'session');
    } catch (e) { setError(e?.response?.data?.detail || 'Could not load session'); }
  };

  const removeSession = async (id) => {
    if (!window.confirm('Delete this practice session?')) return;
    try { await prepAPI.remove(id); await loadSessions(); }
    catch (e) { setError(e?.response?.data?.detail || 'Delete failed'); }
  };

  if (view === 'setup') return <SetupView onCancel={() => setView('home')} onStarted={(s) => { setActiveSession(s); setView('session'); loadSessions(); }} />;
  if (view === 'session' && activeSession) return <SessionView session={activeSession} setSession={setActiveSession} onComplete={(s) => { setActiveSession(s); setView('result'); loadSessions(); }} onExit={() => { setView('home'); loadSessions(); }} />;
  if (view === 'result' && activeSession) return <ResultView session={activeSession} onClose={() => { setView('home'); setActiveSession(null); }} onPracticeAgain={() => setView('setup')} />;

  return (
    <div data-testid="prep-simulator-home">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Brain className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs uppercase tracking-widest text-emerald-400/80">AI Coach</span>
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">Interview Prep Simulator</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm mt-1 max-w-xl">Rehearse real-world interview questions calibrated to your target role and toughness. Get per-answer feedback and an overall scorecard.</p>
        </div>
        <button
          onClick={() => setView('setup')}
          data-testid="start-new-prep-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Start New Practice
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <h2 className="text-base text-slate-600 dark:text-zinc-300 mb-3 mt-2">Recent practice sessions</h2>
      {loading ? (
        <p className="text-slate-500 dark:text-zinc-500 text-sm">Loading...</p>
      ) : sessions.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-10 text-center" data-testid="prep-empty-state">
          <Sparkles className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-400">No practice sessions yet</p>
          <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">Start a session above to rehearse interviews for any role.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {sessions.map(s => (
            <div key={s.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 hover:border-slate-300 dark:hover:border-zinc-700 transition-colors rounded-xl p-4 cursor-pointer" onClick={() => openSession(s.id)} data-testid={`prep-session-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-900 dark:text-white font-medium">{s.role}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">
                    Toughness {s.toughness}/10 · {s.questions?.length || 0} questions · {(s.answers || []).length} answered
                  </p>
                </div>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${s.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>{s.status}</span>
              </div>
              {s.status === 'completed' && (
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500 dark:text-zinc-400">
                  <Trophy className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300" /> Score: {s.overall_score ?? '-'}
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200 dark:border-zinc-800/60">
                <p className="text-[11px] text-slate-500 dark:text-zinc-600">{new Date(s.created_at).toLocaleString()}</p>
                <button onClick={(e) => { e.stopPropagation(); removeSession(s.id); }} className="p-1.5 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-prep-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SetupView({ onCancel, onStarted }) {
  const [role, setRole] = useState('');
  const [skillsText, setSkillsText] = useState('');
  const [toughness, setToughness] = useState(5);
  const [numQuestions, setNumQuestions] = useState(5);
  const [focus, setFocus] = useState('mixed');
  const [jd, setJd] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [criticality, setCriticality] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Prefill from candidate profile if available
    (async () => {
      try {
        const p = await applicantAPI.getProfile();
        const work = p.work_experience || [];
        if (!role && work[0]?.title) setRole(work[0].title);
        if (!skillsText && p.skills?.length) setSkillsText(p.skills.slice(0, 8).join(', '));
      } catch { /* ignore */ }
    })();
  }, []);

  const start = async (e) => {
    e.preventDefault();
    if (!role.trim()) { setError('Please enter a target role'); return; }
    setLoading(true); setError('');
    try {
      const skills = skillsText.split(',').map(s => s.trim()).filter(Boolean);
      const session = await prepAPI.start({
        role, skills,
        toughness: Number(toughness),
        num_questions: Number(numQuestions),
        category_focus: focus,
        job_description: jd,
        target_company: targetCompany.trim(),
        target_role: targetRole.trim() || role,
        criticality,
      });
      onStarted(session);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not start session');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="prep-setup-view" className="max-w-2xl">
      <button onClick={onCancel} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="prep-setup-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Design your practice round</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Jay will generate {numQuestions} question{numQuestions === 1 ? '' : 's'} at the toughness you choose.</p>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}

      <form onSubmit={start} className="space-y-5 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-6">
        <Field label="Target role">
          <input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Senior Backend Engineer" required data-testid="prep-role-input"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
        </Field>

        <Field label="Key skills (comma separated)">
          <input value={skillsText} onChange={e => setSkillsText(e.target.value)} placeholder="Python, FastAPI, MongoDB, System Design" data-testid="prep-skills-input"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
        </Field>

        <Field label={`Toughness — ${toughness}/10 · ${TOUGHNESS_LABELS[toughness]}`}>
          <input type="range" min={0} max={10} value={toughness} onChange={e => setToughness(Number(e.target.value))} data-testid="prep-toughness-slider"
            className="w-full accent-emerald-500" />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Number of questions">
            <select value={numQuestions} onChange={e => setNumQuestions(Number(e.target.value))} data-testid="prep-numq-select"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Focus">
            <select value={focus} onChange={e => setFocus(e.target.value)} data-testid="prep-focus-select"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white">
              <option value="mixed">Mixed</option>
              <option value="technical">Technical</option>
              <option value="behavioral">Behavioral</option>
              <option value="functional">Functional</option>
            </select>
          </Field>
        </div>

        <Field label="Optional: paste a job description for tighter calibration">
          <textarea value={jd} onChange={e => setJd(e.target.value)} rows={3} placeholder="Paste a JD…" data-testid="prep-jd-input"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
        </Field>

        {/* Company-specific prep (optional) */}
        <div className="border-t border-slate-200 dark:border-zinc-800 pt-4">
          <p className="text-xs uppercase tracking-wider text-emerald-500 mb-3 flex items-center gap-1">
            <Target className="w-3 h-3" /> Preparing for a specific company? <span className="text-slate-400 dark:text-zinc-600 normal-case">(optional)</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Target company">
              <input value={targetCompany} onChange={e => setTargetCompany(e.target.value)} placeholder="e.g. Stripe, Google, Razorpay" data-testid="prep-company-input"
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
            </Field>
            <Field label="Target role at this company">
              <input value={targetRole} onChange={e => setTargetRole(e.target.value)} placeholder="e.g. Senior SDE 2" data-testid="prep-target-role-input"
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
            </Field>
          </div>
          <Field label="Criticality">
            <select value={criticality} onChange={e => setCriticality(e.target.value)} data-testid="prep-criticality-select"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white">
              <option value="low">Low — casual practice</option>
              <option value="medium">Medium — regular interview</option>
              <option value="high">High — dream role</option>
              <option value="critical">Critical — final round</option>
            </select>
          </Field>
          <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-1">
            Jay will synthesise questions matching that company's typical hiring loop patterns and never repeat questions within a session.
          </p>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="flex-1 py-2.5 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
          <button type="submit" disabled={loading} data-testid="prep-start-submit"
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <>Start Practice <ChevronRight className="w-4 h-4" /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function SessionView({ session, setSession, onComplete, onExit }) {
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [chargedIdxs, setChargedIdxs] = useState(new Set(session.charged_question_indexes || []));
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [prepCfg, setPrepCfg] = useState({ prep_question_mu: 2 });
  const [balance, setBalance] = useState(null);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const streamRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const baseAnswerRef = React.useRef('');

  const questions = session.questions || [];
  const currentQ = questions[idx];
  const answeredMap = new Map((session.answers || []).map(a => [a.question_index, a]));
  const existing = answeredMap.get(idx);
  const isCharged = chargedIdxs.has(idx) || !!existing; // paid OR already answered
  const chargeAmount = Number(prepCfg?.prep_question_mu ?? 2);

  // Load prep-config (cost) + current balance to inform the modal
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ prepChargeAPI }, { walletAPI }] = await Promise.all([
          import('@/services/paymentsAPI'),
          import('@/services/walletAPI'),
        ]);
        const [c, w] = await Promise.all([prepChargeAPI.getConfig(), walletAPI.me()]);
        if (!mounted) return;
        setPrepCfg(c);
        setBalance(w?.balance ?? 0);
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setAnswer(existing?.answer_text || '');
    setFeedback(existing ? {
      score: existing.score,
      strengths: existing.strengths,
      improvements: existing.improvements,
      model_answer_hint: existing.model_answer_hint,
    } : null);
  }, [idx]);

  // Auto-open the confirm modal when landing on an un-charged question with a charge > 0
  useEffect(() => {
    if (chargeAmount > 0 && !isCharged) {
      setChargeModalOpen(true);
    }
  }, [idx, isCharged, chargeAmount]);

  const confirmCharge = async () => {
    const { prepChargeAPI } = await import('@/services/paymentsAPI');
    const { walletAPI } = await import('@/services/walletAPI');
    try {
      const r = await prepChargeAPI.chargeQuestion(session.id || session._id, idx);
      // Update session and mark charged
      setChargedIdxs(prev => new Set([...prev, idx]));
      setChargeModalOpen(false);
      try { window.dispatchEvent(new Event('mu-wallet-changed')); } catch (_) {}
      if (r?.balance !== undefined) setBalance(r.balance);
      // If server returned a refreshed question text, ensure it's in the session
      if (r?.question) {
        setSession(prev => {
          const qs = [...(prev.questions || [])];
          qs[idx] = r.question;
          return { ...prev, questions: qs, charged_question_indexes: [...(prev.charged_question_indexes || []), idx] };
        });
      }
      // refresh balance regardless
      try { const w = await walletAPI.me(); setBalance(w?.balance ?? balance); } catch { /* */ }
    } catch (e) {
      // Bubble up so PrepChargeConfirmModal displays it
      throw e;
    }
  };

  // Cleanup mic stream on unmount
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const submit = async () => {
    if (!answer.trim()) { setError('Please enter your answer first'); return; }
    setLoading(true); setError('');
    try {
      const fb = await prepAPI.submitAnswer(session.id, idx, answer);
      setFeedback(fb);
      // refresh session so the new answer is in state
      const fresh = await prepAPI.get(session.id);
      setSession(fresh);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not score your answer'); }
    finally { setLoading(false); }
  };

  const finish = async () => {
    setCompleting(true); setError('');
    try {
      const done = await prepAPI.complete(session.id);
      onComplete(done);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to complete'); }
    finally { setCompleting(false); }
  };

  // Voice answer via MediaRecorder + backend Whisper transcription
  const startVoice = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice input not supported in this browser. Type your answer instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      baseAnswerRef.current = answer ? answer.trim() + ' ' : '';

      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onerror = (e) => {
        console.error('MediaRecorder error', e);
        setError('Recording failed. Please try again.');
        stopVoiceTracks();
        setRecording(false);
      };
      mr.onstop = async () => {
        stopVoiceTracks();
        setRecording(false);
        const chunks = audioChunksRef.current;
        if (!chunks.length) return;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size < 1500) {
          setError('Recording too short. Speak a bit longer and try again.');
          return;
        }
        setTranscribing(true);
        try {
          const { transcript } = await prepAPI.transcribe(blob, 'prep-voice.webm');
          if (transcript && transcript.trim()) {
            setAnswer((baseAnswerRef.current + transcript.trim()).trim());
          } else {
            setError('We could not transcribe your audio. Try again or type your answer.');
          }
        } catch (err) {
          setError(err?.response?.data?.detail || 'Transcription failed. Try again or type your answer.');
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecordTime(0);
      timerRef.current = setInterval(() => {
        setRecordTime((t) => {
          if (t >= 119) { // 2 min hard cap
            stopVoice();
            return 120;
          }
          return t + 1;
        });
      }, 1000);
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setError('Microphone permission denied. Allow mic access in the browser address bar and try again.');
      } else if (err?.name === 'NotFoundError') {
        setError('No microphone detected. Connect a mic and retry.');
      } else {
        setError(err?.message || 'Could not start recording.');
      }
    }
  };

  const stopVoiceTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const stopVoice = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      } else {
        stopVoiceTracks();
        setRecording(false);
      }
    } catch (e) {
      stopVoiceTracks();
      setRecording(false);
    }
  };

  const toggleVoice = () => { recording ? stopVoice() : startVoice(); };

  if (!currentQ) return null;
  const answeredCount = (session.answers || []).length;

  return (
    <div data-testid="prep-session-view" className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onExit} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white text-sm flex items-center gap-1" data-testid="prep-session-exit">
          <ChevronLeft className="w-4 h-4" /> Save & Exit
        </button>
        <div className="text-xs text-slate-500 dark:text-zinc-500">{session.role} · Toughness {session.toughness}/10</div>
      </div>

      <div className="flex items-center gap-1 mb-5" data-testid="prep-progress-dots">
        {questions.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} title={`Question ${i + 1}`}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i === idx ? 'bg-emerald-400' : answeredMap.has(i) ? 'bg-emerald-500/40' : 'bg-slate-100 dark:bg-zinc-800'}`} />
        ))}
      </div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">{currentQ.category || 'general'}</span>
          {currentQ.difficulty && <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">{currentQ.difficulty}</span>}
          <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ml-auto">Q{idx + 1} of {questions.length}</span>
        </div>
        {isCharged || chargeAmount === 0 ? (
          <p className="text-slate-900 dark:text-white text-lg leading-relaxed" data-testid="prep-question-text">{currentQ.question}</p>
        ) : (
          <div className="py-6 text-center" data-testid="prep-question-locked">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-500/10 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-slate-900 dark:text-white font-medium mb-1">Question locked</p>
            <p className="text-slate-500 dark:text-zinc-500 text-sm mb-4">
              Confirm the {chargeAmount} Mu charge to reveal the next question.
            </p>
            <button
              onClick={() => setChargeModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium"
              data-testid="prep-reveal-question-btn"
            >
              Reveal for {chargeAmount} Mu
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-6 mb-4">
        <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-2 uppercase tracking-wide">Your answer</label>
        <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={6} placeholder="Type or dictate your response…" data-testid="prep-answer-input"
          className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <button
            onClick={toggleVoice}
            disabled={transcribing}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs disabled:opacity-60 ${recording ? 'bg-red-500/15 text-red-700 dark:text-red-300 ring-1 ring-red-500/40' : transcribing ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
            data-testid="prep-voice-toggle"
          >
            {transcribing ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…</>
            ) : recording ? (
              <><MicOff className="w-3.5 h-3.5" /> Stop ({Math.floor(recordTime / 60)}:{String(recordTime % 60).padStart(2, '0')})</>
            ) : (
              <><Mic className="w-3.5 h-3.5" /> Use voice</>
            )}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => { setAnswer(''); setFeedback(null); }} className="px-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="prep-clear-answer">
              <RotateCcw className="w-3.5 h-3.5 inline mr-1" /> Clear
            </button>
            <button onClick={submit} disabled={loading} data-testid="prep-submit-answer"
              className="px-4 py-1.5 rounded-lg text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring…</> : <>Get feedback <ChevronRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}

      {feedback && (
        <div className="bg-gradient-to-br from-emerald-500/10 via-white dark:via-[#111114] to-white dark:to-[#111114] border border-emerald-500/30 rounded-xl p-6 mb-4 animate-in fade-in" data-testid="prep-feedback-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h3 className="text-slate-900 dark:text-white font-medium">Jay&apos;s feedback</h3>
            </div>
            <div className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400" data-testid="prep-feedback-score">{feedback.score}</div>
          </div>
          {feedback.strengths && <FeedbackBlock label="Strengths" text={feedback.strengths} tone="positive" />}
          {feedback.improvements && <FeedbackBlock label="Improvements" text={feedback.improvements} tone="warn" />}
          {feedback.model_answer_hint && <FeedbackBlock label="Approach to try" text={feedback.model_answer_hint} tone="info" />}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-6">
        <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="prep-prev-btn"
          className="px-4 py-2 rounded-lg text-sm bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 disabled:opacity-40 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <div className="text-xs text-slate-500 dark:text-zinc-500">{answeredCount} / {questions.length} answered</div>
        {idx < questions.length - 1 ? (
          <button onClick={() => setIdx(idx + 1)} data-testid="prep-next-btn"
            className="px-4 py-2 rounded-lg text-sm bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 flex items-center gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={finish} disabled={completing || answeredCount === 0} data-testid="prep-finish-btn"
            className="px-4 py-2 rounded-lg text-sm bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-50 flex items-center gap-1.5">
            {completing ? <><Loader2 className="w-4 h-4 animate-spin" /> Finishing…</> : <>Finish & Get scorecard <CheckCircle2 className="w-4 h-4" /></>}
          </button>
        )}
      </div>

      <PrepChargeConfirmModal
        isOpen={chargeModalOpen}
        onClose={() => setChargeModalOpen(false)}
        onConfirm={confirmCharge}
        amountMu={chargeAmount}
        balance={balance}
      />
    </div>
  );
}

function FeedbackBlock({ label, text, tone }) {
  const colors = {
    positive: 'text-emerald-700 dark:text-emerald-300',
    warn: 'text-amber-700 dark:text-amber-300',
    info: 'text-sky-700 dark:text-sky-300',
  };
  return (
    <div className="mb-3 last:mb-0">
      <p className={`text-[11px] uppercase tracking-wide mb-0.5 ${colors[tone] || 'text-slate-500 dark:text-zinc-400'}`}>{label}</p>
      <p className="text-sm text-slate-700 dark:text-zinc-200 leading-relaxed">{text}</p>
    </div>
  );
}

function ResultView({ session, onClose, onPracticeAgain }) {
  const overall = session.overall_score ?? 0;
  const stat = (label, val, color) => (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-4">
      <p className="text-xs text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${color}`}>{val ?? 0}</p>
    </div>
  );

  return (
    <div data-testid="prep-result-view" className="max-w-3xl">
      <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white text-sm flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to sessions
      </button>

      <div className="bg-gradient-to-br from-emerald-500/15 via-white dark:via-[#111114] to-white dark:to-[#111114] border border-emerald-500/30 rounded-2xl p-8 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-5 h-5 text-amber-700 dark:text-amber-300" />
          <span className="text-xs uppercase tracking-widest text-emerald-300/80">Scorecard</span>
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">{session.role}</h1>
        <p className="text-slate-500 dark:text-zinc-400 text-sm mt-1">Toughness {session.toughness}/10 · {session.questions?.length || 0} questions · {(session.answers || []).length} answered</p>
        <div className="mt-6 flex items-end gap-3">
          <span className="text-6xl font-bold text-emerald-700 dark:text-emerald-300" data-testid="prep-overall-score">{overall}</span>
          <span className="text-slate-500 dark:text-zinc-400 mb-2">overall</span>
        </div>
        {session.summary && <p className="text-slate-600 dark:text-zinc-300 text-sm leading-relaxed mt-4 max-w-xl">{session.summary}</p>}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {stat('Functional', session.functional_score, 'text-sky-700 dark:text-sky-300')}
        {stat('Technical', session.technical_score, 'text-emerald-700 dark:text-emerald-300')}
        {stat('Behavioral', session.behavioral_score, 'text-amber-700 dark:text-amber-300')}
      </div>

      <h2 className="text-base text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Per-question feedback</h2>
      <div className="space-y-3">
        {(session.answers || []).sort((a, b) => a.question_index - b.question_index).map(a => (
          <div key={a.question_index} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-4" data-testid={`prep-result-q-${a.question_index}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">{a.category}</span>
              <span className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">{a.score}/100</span>
            </div>
            <p className="text-slate-900 dark:text-white text-sm mb-2">{a.question_text}</p>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-2 italic">&ldquo;{a.answer_text}&rdquo;</p>
            {a.strengths && <p className="text-emerald-200/90 text-xs mb-1"><b>+</b> {a.strengths}</p>}
            {a.improvements && <p className="text-amber-200/90 text-xs mb-1"><b>→</b> {a.improvements}</p>}
            {a.model_answer_hint && <p className="text-sky-200/90 text-xs"><b>Hint:</b> {a.model_answer_hint}</p>}
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <button onClick={onPracticeAgain} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg text-sm font-medium" data-testid="prep-practice-again">
          Practice another round
        </button>
        <button onClick={onClose} className="flex-1 py-2.5 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">
          Done
        </button>
      </div>
    </div>
  );
}
